import Ajv2020, { type ErrorObject, type ValidateFunction } from "ajv/dist/2020";
import addFormats from "ajv-formats";
import boardContextImportSchema from "../../schemas/v1/board-context-import.schema.json";
import ceoReviewExportSchema from "../../schemas/v1/ceo-review-export.schema.json";
import dataExportPackageSchema from "../../schemas/v1/data-export-package.schema.json";
import dataImportReportSchema from "../../schemas/v1/data-import-report.schema.json";
import jointCorrectionSchema from "../../schemas/v1/joint-correction.schema.json";
import { CONTRACT_SCHEMA_VERSION } from "../domain/common";
import type { ContractEnvelope } from "../domain/types";
import { RuleErrorCode } from "../rules/errors";

export type ContractSchemaName = ContractEnvelope<unknown>["schema_name"];

export interface ContractValidationError {
  code: string;
  path: string;
  message: string;
}

export type ContractValidationResult<T = unknown> =
  { ok: true; value: T } | { ok: false; errors: ContractValidationError[] };

const ajv = new Ajv2020({
  allErrors: true,
  strict: true,
  validateFormats: true,
});
addFormats(ajv);

const validators: Record<ContractSchemaName, ValidateFunction> = {
  "ceo-review-export": ajv.compile(ceoReviewExportSchema),
  "board-context-import": ajv.compile(boardContextImportSchema),
  "joint-correction": ajv.compile(jointCorrectionSchema),
  "data-export-package": ajv.compile(dataExportPackageSchema),
  "data-import-report": ajv.compile(dataImportReportSchema),
};

function fromAjvError(error: ErrorObject): ContractValidationError {
  return {
    code: RuleErrorCode.CONTRACT_SCHEMA_INVALID,
    path: error.instancePath || "/",
    message: error.message ?? "合同格式错误。",
  };
}

function getEnvelopeHeader(value: unknown): {
  schemaName?: string;
  schemaVersion?: string;
} {
  if (value === null || typeof value !== "object") {
    return {};
  }
  const candidate = value as Record<string, unknown>;
  return {
    ...(typeof candidate.schema_name === "string" ? { schemaName: candidate.schema_name } : {}),
    ...(typeof candidate.schema_version === "string"
      ? { schemaVersion: candidate.schema_version }
      : {}),
  };
}

export function validateContract<T = unknown>(
  value: unknown,
  expectedSchemaName?: ContractSchemaName,
): ContractValidationResult<T> {
  const header = getEnvelopeHeader(value);
  if (header.schemaVersion !== CONTRACT_SCHEMA_VERSION) {
    return {
      ok: false,
      errors: [
        {
          code: RuleErrorCode.UNSUPPORTED_CONTRACT_VERSION,
          path: "/schema_version",
          message: `不支持合同版本 ${header.schemaVersion ?? "<missing>"}；当前支持 ${CONTRACT_SCHEMA_VERSION}。`,
        },
      ],
    };
  }

  if (expectedSchemaName !== undefined && header.schemaName !== expectedSchemaName) {
    return {
      ok: false,
      errors: [
        {
          code: RuleErrorCode.CONTRACT_SCHEMA_INVALID,
          path: "/schema_name",
          message: `期望 ${expectedSchemaName}，实际为 ${header.schemaName ?? "<missing>"}。`,
        },
      ],
    };
  }

  if (header.schemaName === undefined || !(header.schemaName in validators)) {
    return {
      ok: false,
      errors: [
        {
          code: RuleErrorCode.CONTRACT_SCHEMA_INVALID,
          path: "/schema_name",
          message: "未知合同名称。",
        },
      ],
    };
  }

  const validator = validators[header.schemaName as ContractSchemaName];
  const valid = validator(value);
  if (!valid) {
    return {
      ok: false,
      errors: (validator.errors ?? []).map(fromAjvError),
    };
  }
  return { ok: true, value: value as T };
}

export function assertContract<T = unknown>(
  value: unknown,
  expectedSchemaName?: ContractSchemaName,
): T {
  const result = validateContract<T>(value, expectedSchemaName);
  if (!result.ok) {
    throw new Error(
      result.errors.map((item) => `${item.code} ${item.path}: ${item.message}`).join("; "),
    );
  }
  return result.value;
}
